// packages/web-ui/src/state/settings.ts
//
// W6-S2 AC #12 — settings zustand slice. Holds the merged-settings snapshot,
// the user's pendingChanges (the source of truth for "typed but not saved"),
// the active scope/category, and the per-key reveal state.
//
// The slice is a vanilla zustand store factory (mirrors the changeset/kb
// slice pattern) so component tests can inject a clean store without
// reaching into the root composer.

import { createStore, type StoreApi } from 'zustand/vanilla';

import type { Result } from '@tessa-lean/base';
import type {
  MergedSettings,
  ResolvedSettingEntry,
  SettingsBatchPutRequest,
  SettingsScope,
} from '@tessa-lean/base/api-schemas/index.js';

import {
  settingsApi as defaultSettingsApi,
  type SettingsApi,
} from '../api/clients/settings.js';
import type { ApiContext, ApiError } from '../api/client.js';

/** UI-level scope selector. Differs from the on-disk scope (no 'both' write). */
export type ActiveScope = 'workspace' | 'user' | 'both';

export interface SettingsSliceState {
  /** Last-fetched merged settings. `null` until first load. */
  merged: MergedSettings | null;
  /** Status of the most-recent fetch. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Populated when status === 'error'. */
  errorMessage?: string;
  /** Keys whose source is `'sys-jarvis (deprecated)'`. */
  legacyKeysDetected: ReadonlyArray<string>;
  /** In-memory diff: keys the user has typed but not yet saved. */
  pendingChanges: Map<string, unknown>;
  /** UI-level scope filter (drives row enable/disable). */
  activeScope: ActiveScope;
  /** Highlighted category in the left rail. `null` until first selection. */
  activeCategory: string | null;
  /** Live search query. Empty string = no filter. */
  searchQuery: string;
  /** The single secret key currently revealed (only one at a time). */
  revealedSecretKey: string | null;
  /** The plaintext for the revealed secret — held in state ONLY during the
   *  10 s reveal window; cleared by the timer. Note: this still flows through
   *  zustand state, but the regression test in SecretRow asserts the
   *  serialised state never re-enters it after a save. */
  revealedSecretValue: string | null;
}

export interface SettingsSliceActions {
  /** Fetches merged settings; updates `merged` + `legacyKeysDetected`. */
  loadMergedSettings: () => Promise<void>;
  /** Sets a pending change. If value === merged value, removes the entry. */
  setPendingChange: (qualifiedKey: string, value: unknown) => void;
  /** Clears pending changes (Cancel + post-save). */
  clearPendingChanges: () => void;
  /**
   * Submit pendingChanges as a batch. Returns Result for the panel.
   *
   * Fix #3 — `providerRestarted` / `providerRestartReason` echo the server's
   * response so the panel can render a confirmation banner when a provider-
   * touching write triggered an in-place chat-service restart.
   */
  saveChanges: (
    scope: SettingsScope,
  ) => Promise<
    Result<
      { written: number; providerRestarted?: boolean; providerRestartReason?: string },
      ApiError
    >
  >;
  /** Sets the active scope filter; clears pendingChanges first. */
  setActiveScope: (scope: ActiveScope) => void;
  /** Sets the active category in the left rail. */
  setActiveCategory: (category: string | null) => void;
  /** Sets the live search query. */
  setSearchQuery: (q: string) => void;
  /** Reveals a secret value via the per-key endpoint; auto-clears after 10 s. */
  revealSecret: (qualifiedKey: string) => Promise<Result<{ value: string }, ApiError>>;
  /** Clears the reveal state (called by the timer or on key switch). */
  clearRevealedSecret: () => void;
  /** Migrate-key flow ([Migrate now] / [Keep both]). */
  applyMigration: (input: {
    legacyKey: string;
    newKey: string;
    scope: SettingsScope;
    deleteOriginal: boolean;
  }) => Promise<Result<{ written: boolean }, ApiError>>;
  /** Test/debug helper. */
  _reset: () => void;
}

export type SettingsSlice = SettingsSliceState & SettingsSliceActions;

export const SETTINGS_INITIAL_STATE: SettingsSliceState = {
  merged: null,
  status: 'idle',
  errorMessage: undefined,
  legacyKeysDetected: [],
  pendingChanges: new Map(),
  activeScope: 'both',
  activeCategory: null,
  searchQuery: '',
  revealedSecretKey: null,
  revealedSecretValue: null,
};

export interface SettingsSliceConfig {
  api?: SettingsApi;
  apiContext?: ApiContext;
  /** Test seam — override the reveal-clear timeout (default 10000 ms). */
  revealTimeoutMs?: number;
  /** Test seam — override `setTimeout` so fake timers can step the reveal. */
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
}

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

/** Convert merged value lookups to a normalised map. */
function valueOfEntry(entry: ResolvedSettingEntry): unknown {
  if ('value' in entry && entry.value !== undefined) return entry.value;
  return entry.defaultValue;
}

/**
 * True when the pending value matches the merged value (so the diff entry
 * should be deleted from `pendingChanges`).
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => valuesEqual(v, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

/**
 * Zustand StateCreator for the settings slice. Used by the standalone
 * vanilla store factory (`createSettingsStore`) and may also compose into
 * the root store when its parent merge story (W2-S2 root composer) wires
 * the slice in alongside `chat`/`kb`/`changeset`.
 */
export function createSettingsStore(
  config: SettingsSliceConfig = {},
): StoreApi<SettingsSlice> {
  const api = config.api ?? defaultSettingsApi;
  const ctx = config.apiContext ?? defaultApiContext();
  const revealMs = config.revealTimeoutMs ?? 10_000;
  const setTimeoutImpl = config.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = config.clearTimeoutImpl ?? clearTimeout;

  return createStore<SettingsSlice>((set, get) => {
    let revealTimer: ReturnType<typeof setTimeout> | null = null;

    const clearRevealTimer = (): void => {
      if (revealTimer !== null) {
        clearTimeoutImpl(revealTimer);
        revealTimer = null;
      }
    };

    return {
      ...SETTINGS_INITIAL_STATE,

      loadMergedSettings: async () => {
        set((s) => ({ ...s, status: 'loading' as const }));
        const res = await api.getMergedSettings(ctx);
        if (!res.ok) {
          set((s) => ({
            ...s,
            status: 'error' as const,
            errorMessage: res.error.message,
          }));
          return;
        }
        const legacy = res.value.entries
          .filter((e) => e.source === 'sys-jarvis (deprecated)')
          .map((e) => e.key);
        set((s) => ({
          ...s,
          status: 'ready' as const,
          errorMessage: undefined,
          merged: res.value,
          legacyKeysDetected: legacy,
        }));
      },

      setPendingChange: (qualifiedKey, value) => {
        const merged = get().merged;
        const next = new Map(get().pendingChanges);
        // Look up the merged value to short-circuit no-op edits.
        let mergedValue: unknown;
        if (merged !== null) {
          const entry = merged.entries.find((e) => e.key === qualifiedKey);
          if (entry !== undefined) mergedValue = valueOfEntry(entry);
        }
        if (valuesEqual(value, mergedValue)) {
          next.delete(qualifiedKey);
        } else {
          next.set(qualifiedKey, value);
        }
        set((s) => ({ ...s, pendingChanges: next }));
      },

      clearPendingChanges: () => {
        set((s) => ({ ...s, pendingChanges: new Map() }));
      },

      saveChanges: async (scope) => {
        const pending = get().pendingChanges;
        if (pending.size === 0) {
          return { ok: true, value: { written: 0 } };
        }
        const updates: SettingsBatchPutRequest['updates'] = Array.from(pending.entries()).map(
          ([key, value]) => ({ key, value }),
        );
        const res = await api.batchUpdate(ctx, { scope, updates });
        if (res.ok) {
          set((s) => ({ ...s, pendingChanges: new Map() }));
          // Refresh the merged snapshot on success.
          await get().loadMergedSettings();
          return {
            ok: true,
            value: {
              written: res.value.written,
              ...(res.value.providerRestarted !== undefined
                ? { providerRestarted: res.value.providerRestarted }
                : {}),
              ...(res.value.providerRestartReason !== undefined
                ? { providerRestartReason: res.value.providerRestartReason }
                : {}),
            },
          };
        }
        return { ok: false, error: res.error };
      },

      setActiveScope: (scope) => {
        set((s) => ({ ...s, activeScope: scope, pendingChanges: new Map() }));
      },

      setActiveCategory: (category) => {
        set((s) => ({ ...s, activeCategory: category }));
      },

      setSearchQuery: (q) => {
        set((s) => ({ ...s, searchQuery: q }));
      },

      revealSecret: async (qualifiedKey) => {
        clearRevealTimer();
        // Optimistically wipe any previously-revealed value.
        set((s) => ({
          ...s,
          revealedSecretKey: null,
          revealedSecretValue: null,
        }));
        const res = await api.revealSecret(ctx, qualifiedKey);
        if (!res.ok) return res;
        set((s) => ({
          ...s,
          revealedSecretKey: qualifiedKey,
          revealedSecretValue: res.value.value,
        }));
        revealTimer = setTimeoutImpl(() => {
          set((s) => ({
            ...s,
            revealedSecretKey: null,
            revealedSecretValue: null,
          }));
          revealTimer = null;
        }, revealMs);
        return { ok: true, value: { value: res.value.value } };
      },

      clearRevealedSecret: () => {
        clearRevealTimer();
        set((s) => ({
          ...s,
          revealedSecretKey: null,
          revealedSecretValue: null,
        }));
      },

      applyMigration: async (input) => {
        const res = await api.migrateKey(ctx, input);
        if (res.ok) {
          // Re-fetch so `source` flips and the badge clears.
          await get().loadMergedSettings();
          return { ok: true, value: { written: res.value.written } };
        }
        return { ok: false, error: res.error };
      },

      _reset: () => {
        clearRevealTimer();
        set(() => ({ ...SETTINGS_INITIAL_STATE, pendingChanges: new Map() }));
      },
    };
  });
}

// ───────────────────────────────────────────────────────────────────────────
// Selectors
// ───────────────────────────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────────────────────
// Shared singleton — readers outside the settings panel (e.g. the chat-panel
// composer reading `llm.contextWindows`) consume the merged settings via
// this default store so they don't trip on the SettingsPanel's mount/unmount
// lifecycle. App.tsx kicks off `loadMergedSettings()` once on first mount so
// the data is ready by the time other panels render.
// ───────────────────────────────────────────────────────────────────────────

let defaultSettingsStore: StoreApi<SettingsSlice> | null = null;

export function getDefaultSettingsStore(): StoreApi<SettingsSlice> {
  defaultSettingsStore ??= createSettingsStore();
  return defaultSettingsStore;
}

/** Test-only — wipe the singleton between cases. */
export function _resetDefaultSettingsStoreForTests(): void {
  defaultSettingsStore = null;
}

export function selectPendingCount(state: SettingsSlice): number {
  return state.pendingChanges.size;
}

export function selectMergedByKey(
  state: SettingsSlice,
  qualifiedKey: string,
): ResolvedSettingEntry | undefined {
  return state.merged?.entries.find((e) => e.key === qualifiedKey);
}

/**
 * The legacy per-provider settings keys (`azure.*` / `bedrock.*` / `provider`)
 * are superseded by the LLM Configurations list (a dedicated profile store).
 * Their categories ("Providers", "Providers / *") are hidden from the generic
 * settings UI so there is one canonical place to configure providers.
 */
export function isProviderSettingsCategory(category: string): boolean {
  return category.startsWith('Providers');
}

/** Categories derived from merged.entries — alphabetical, with key counts. */
export function selectCategories(
  state: SettingsSlice,
): ReadonlyArray<{ slug: string; label: string; keyCount: number }> {
  if (state.merged === null) return [];
  const counts = new Map<string, number>();
  for (const entry of state.merged.entries) {
    if (isProviderSettingsCategory(entry.category)) continue;
    counts.set(entry.category, (counts.get(entry.category) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, keyCount]) => ({ slug: categoryToSlug(label), label, keyCount }));
}

/** Slug helper inlined for the selector. */
function categoryToSlug(category: string): string {
  return category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Visible keys after applying search + scope + category filters. */
export function selectVisibleKeys(state: SettingsSlice): ReadonlyArray<string> {
  if (state.merged === null) return [];
  const query = state.searchQuery.trim().toLowerCase();
  const tokens = query.length > 0 ? query.split(/\s+/) : [];
  return state.merged.entries
    .filter((e) => {
      // Provider keys are surfaced by the dedicated LLM Configurations section.
      if (isProviderSettingsCategory(e.category)) return false;
      // Scope filter: when activeScope === 'both', include all entries; otherwise
      // include only entries whose schema scope matches OR is 'both'.
      if (state.activeScope === 'workspace') {
        if (e.scope !== 'workspace' && e.scope !== 'both') return false;
      } else if (state.activeScope === 'user') {
        if (e.scope !== 'user' && e.scope !== 'both') return false;
      }
      // Search filter
      if (tokens.length > 0) {
        const hay = `${e.key} ${e.description} ${e.category} ${(e.enum ?? []).join(' ')}`.toLowerCase();
        for (const t of tokens) if (!hay.includes(t)) return false;
      }
      return true;
    })
    .map((e) => e.key);
}
